import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonPrimary, StackHorizontal } from '@talend/design-system';
import { getTheme, List } from '@talend/react-components';

import { I18N_NAME_SPACE } from '../../../../constants';
import { CustomAttributeDefinition } from '../../CAD.types';
import {
	CAD_LIST_ROW_LARGE_HEIGHT,
	DATA_FEATURES,
	LOCAL_STORAGE_CAD_DISPLAY_MODE,
} from '../../service/CADList/CADList.constants';
import { CADDeleteDialog } from '../CADDeleteDialog';
import { getColumns, getExpandedSortOptions, getTypeIcon, getTypeLabel } from './CADList.columns';

import theme from './CADList.module.css';

const css = getTheme(theme);

const deleteCADButton = (deleteEntitlement: boolean, onClick: Function, t: Function) => {
	return {
		available: deleteEntitlement,
		icon: 'talend-trash',
		id: 'cad-delete-button',
		label: t('REMOVE_CAD', 'Remove this custom attribute'),
		onClick,
	};
};

function convertItems(t: Function, items: CustomAttributeDefinition[]) {
	return items.map(({ type, createdBy = {}, updatedBy = {}, ...rest }) => ({
		...rest,
		type,
		createdBy: createdBy.userName,
		updatedBy: updatedBy.userName,
		typeLabel: getTypeLabel(t, type),
	}));
}

export interface CADListMapProps {
	createEntitlement: boolean;
	deleteEntitlement: boolean;
	deleteInProgress?: boolean;
	items: CustomAttributeDefinition[];
	loading?: boolean;
	showDeleteModal?: boolean;
}

export interface CADListDispatchProps {
	cleanCustomAttributeDefinitions: Function;
	fetchCustomAttributeDefinitions: Function;
	onAdd: () => void;
	onClickTitle: Function;
	onClickTrash: Function;
	onCloseDeleteModal: () => void;
	onValidateDeleteCustomAttribute: (id: string, label: string) => void;
}

export type CADListProps = CADListMapProps & CADListDispatchProps;

export function CADList({
	cleanCustomAttributeDefinitions,
	createEntitlement,
	deleteEntitlement,
	deleteInProgress,
	fetchCustomAttributeDefinitions,
	items,
	loading = true,
	onAdd,
	onClickTitle,
	onClickTrash,
	onCloseDeleteModal,
	showDeleteModal,
	onValidateDeleteCustomAttribute,
}: CADListProps) {
	const { t } = useTranslation(I18N_NAME_SPACE);
	const [deleteDialog, setDeleteDialog] = useState<CustomAttributeDefinition>();
	const openDeleteDialog = (entity: CustomAttributeDefinition) => {
		setDeleteDialog(entity);
	};

	const collection = useMemo(() => convertItems(t, items), [t, items]);
	const collectionWithActions = List.hooks.useCollectionActions(
		collection,
		(row: CustomAttributeDefinition) => [
			deleteCADButton(
				deleteEntitlement,
				() => {
					onClickTrash();
					openDeleteDialog(row);
				},
				t,
			),
		],
	);

	useEffect(() => {
		fetchCustomAttributeDefinitions();
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return (): void => cleanCustomAttributeDefinitions();
	}, [fetchCustomAttributeDefinitions, cleanCustomAttributeDefinitions]);

	const [displayMode, setDisplayMode] = List.hooks.useDisplayMode(
		LOCAL_STORAGE_CAD_DISPLAY_MODE,
		'table',
	);

	const columns = getColumns(t);

	return (
		<>
			<CADDeleteDialog
				id={deleteDialog?.id || ''}
				label={deleteDialog?.label || ''}
				type={deleteDialog?.type}
				inProgress={deleteInProgress}
				onCancel={onCloseDeleteModal}
				onValidate={onValidateDeleteCustomAttribute}
				show={showDeleteModal}
			/>
			<List.Manager
				t={t}
				id="cad-list-manager"
				collection={collectionWithActions}
				initialSortParams={{ sortBy: columns.label.dataKey, isDescending: false }}
			>
				<List.Toolbar>
					<StackHorizontal gap={0}>
						{createEntitlement ? (
							<ButtonPrimary
								icon="plus"
								id="attribute:add:open"
								onClick={onAdd}
								data-feature={DATA_FEATURES.ADD}
							>
								{t('CAD_LIST_ADD', 'Add custom attribute')}
							</ButtonPrimary>
						) : null}
					</StackHorizontal>
					<List.Toolbar.Right>
						{displayMode === List.constants.DISPLAY_MODE.LARGE && (
							<List.SortBy
								options={getExpandedSortOptions(t)}
								initialValue={{
									sortBy: columns.label.dataKey,
									isDescending: false,
								}}
							/>
						)}
						<List.TextFilter
							placeholder={t('CAD_FIND_PLACEHOLDER', {
								defaultValue: 'Find a custom attribute',
							})}
						/>
						<List.DisplayMode
							// eslint-disable-next-line @typescript-eslint/no-unsafe-return
							onChange={(_: any, value: string) => setDisplayMode(value)}
							selectedDisplayMode={displayMode}
						/>
					</List.Toolbar.Right>
				</List.Toolbar>
				<div className={css('cad-vlist')}>
					<List.VList
						virtualized
						id="cad-list"
						selectionToggle={() => {}}
						onRowDoubleClick={onClickTitle}
						inProgress={loading}
						rowHeight={
							displayMode === List.constants.DISPLAY_MODE.LARGE
								? CAD_LIST_ROW_LARGE_HEIGHT
								: undefined
						}
						type={displayMode.toUpperCase()}
					>
						<List.VList.Title
							{...columns.label}
							columnData={{
								onClick: onClickTitle,
								actionsKey: 'actions',
								'data-feature': createEntitlement ? DATA_FEATURES.EDIT : DATA_FEATURES.VIEW,
							}}
						/>
						<List.VList.Text {...columns.description} />
						{displayMode === List.constants.DISPLAY_MODE.LARGE ? (
							<List.VList.Text {...columns.type} />
						) : (
							<List.VList.IconText
								{...columns.type}
								columnData={{
									getIcon: getTypeIcon,
								}}
								width={140}
							/>
						)}

						<List.VList.Boolean
							{...columns.mandatory}
							width={75}
							columnData={{
								displayMode:
									displayMode === List.constants.DISPLAY_MODE.LARGE
										? List.VList.Boolean.displayMode.TEXT
										: List.VList.Boolean.displayMode.ICON,
							}}
						/>
						<List.VList.Datetime {...columns.createdAt} columnData={{ mode: 'format' }} />
						<List.VList.Text {...columns.createdBy} />
						<List.VList.Datetime {...columns.updatedAt} columnData={{ mode: 'format' }} />
						<List.VList.Text {...columns.updatedBy} />
					</List.VList>
				</div>
			</List.Manager>
		</>
	);
}

CADList.displayName = 'CADList';
